import Chrysler from '@carvana/ds-icons/Chrysler';
import Jeep from '@carvana/ds-icons/Jeep';
import Ram from '@carvana/ds-icons/Ram';
import RamHorizontal from '@carvana/ds-icons/RamHorizontal';
import { icon } from '@carvana/ds-styles';
import cx from 'classnames';
import { useIsMobile } from '~/context/common/DeviceContext';
import DodgeIcon from '~/icons/new-car/DodgeIcon';

const iconSizeClasses: Record<string, string> = {
  Chrysler: 'w-[78px] h-[78px] sm:w-9xl sm:h-9xl md:w-[258px] md:h-[258px]',
  Dodge: 'w-64 h-64 sm:w-9xl sm:h-9xl md:w-[258px] md:h-[258px]',
  Jeep: 'w-64 h-64 sm:w-9xl sm:h-9xl md:w-[184px] md:h-[184px]',
  Ram: 'w-64 h-64 sm:w-9xl sm:h-9xl md:w-[258px] md:h-[258px]'
};

// DS icons hardcode their own path fill and ignore the color prop, so override it via CSS.
const FILL_OVERRIDE_CLASSES = '[&_path]:fill-[var(--icon-fill)]';
const fillOverrideStyle = {
  '--icon-fill': icon.inverse
} as React.CSSProperties;

const makeIcons: Record<string, typeof Chrysler> = {
  Chrysler,
  Jeep,
  Ram: RamHorizontal
};

// Below the sm breakpoint (768px), Ram renders as a stacked/vertical logo instead.
const makeMobileIcons: Record<string, typeof Chrysler> = {
  Ram
};

type OwnProps = {
  make: string;
};

export default function MakeHeroIcon({ make }: OwnProps) {
  const isMobile = useIsMobile();
  const sizeClasses = iconSizeClasses[make];

  if (!sizeClasses) {
    return null;
  }

  if (make === 'Dodge') {
    return (
      <DodgeIcon
        arrowUsesFill
        aria-label="Dodge logo"
        className={sizeClasses}
        color={icon.inverse}
        orientation={isMobile ? 'vertical' : 'horizontal'}
        role="img"
      />
    );
  }

  const Icon = isMobile
    ? makeMobileIcons[make] ?? makeIcons[make]
    : makeIcons[make];

  if (!Icon) {
    return null;
  }

  return (
    <Icon
      aria-label={`${make} logo`}
      className={cx(sizeClasses, FILL_OVERRIDE_CLASSES)}
      role="img"
      style={fillOverrideStyle}
    />
  );
}
